import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, Button, IconButton, LinearProgress, Stack, Typography, useMediaQuery } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CarouselBackdrop from 'components/shared/carousels/CarouselBackdrop';
import routes from 'common/routes';

const AUTOPLAY_MS = 7000;

const clamp = (lines) => ({
  display: '-webkit-box',
  WebkitLineClamp: lines,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
});

const slideIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// Reserva de alto del titular (2 líneas) para que la card NO salte de tamaño entre slides.
const TITLE_LINE = 1.2;

/**
 * Destacado de noticias del dashboard: card OSCURA en ambos modos con la imagen de la
 * noticia difuminada de fondo (lenguaje pedido por el usuario), tamaño uniforme entre
 * slides, loop infinito circular con crossfade y autoplay que se pausa al interactuar.
 */
function FeaturedNewsCarousel() {
  const { t, i18n } = useTranslation(['news', 'common']);
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const allNews = useSelector((state) => state.news.data.news);
  const items = useMemo(() => (allNews || []).slice(0, 5), [allNews]);

  // Autoplay circular: se pausa en hover/focus y con prefers-reduced-motion.
  useEffect(() => {
    if (items.length < 2 || paused || prefersReducedMotion) return undefined;

    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, AUTOPLAY_MS);

    return () => clearInterval(timer);
  }, [items.length, paused, prefersReducedMotion]);

  if (items.length === 0) return null;

  const current = items[Math.min(index, items.length - 1)];
  const image = current.featured_image || current.thumbnail || null;

  const formattedDate = current.created_at
    ? new Date(current.created_at).toLocaleDateString(i18n.language === 'es' ? 'es-CO' : 'en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : null;

  const goTo = (next) => setIndex((prev) => (prev + next + items.length) % items.length);

  // La card es una superficie OSCURA fija en ambos modos (backdrop difuminado + scrim):
  // los textos van en blanco fijo y el CTA en blanco/negro fijo, NO con tokens que se
  // invierten en claro. El dorado usa `gold.bright` (pensado para superficies oscuras).
  return (
    <Box
      role="region"
      aria-roledescription="carrusel"
      aria-label={t('featuredNews', { ns: 'news', defaultValue: 'Noticias destacadas' })}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        p: { xs: 2.5, md: 4 },
        borderRadius: '16px',
        backgroundColor: '#101214',
        border: '1px solid',
        borderColor: 'hairline.main',
        boxShadow: 'var(--otx-card-shadow)',
        minHeight: { xs: 420, md: 340 },
        display: 'flex',
      }}>
      <CarouselBackdrop image={image} alt="" />

      <Stack
        key={current.id}
        direction={{ xs: 'column-reverse', md: 'row' }}
        spacing={{ xs: 2.5, md: 4 }}
        alignItems="stretch"
        aria-live="polite"
        sx={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          minWidth: 0,
          animation: `${slideIn} 250ms cubic-bezier(0.32, 0.72, 0, 1)`,
        }}>
        {/* Texto */}
        <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0, justifyContent: 'center' }}>
          {formattedDate && (
            <Stack direction="row" spacing={0.75} alignItems="center">
              <CalendarMonthOutlinedIcon sx={{ fontSize: 16, color: 'gold.bright' }} />
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.72)', fontWeight: 700 }}>
                {formattedDate}
              </Typography>
            </Stack>
          )}
          <Typography
            component="h3"
            sx={{
              color: '#FFFFFF',
              fontWeight: 800,
              letterSpacing: '-0.01em',
              lineHeight: TITLE_LINE,
              fontSize: { xs: '1.3rem', md: '1.6rem' },
              minHeight: { xs: `${TITLE_LINE * 2 * 1.3}rem`, md: `${TITLE_LINE * 2 * 1.6}rem` },
              ...clamp(2),
            }}>
            {current.title}
          </Typography>
          {current.summary && (
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.72)', ...clamp(3) }}>
              {current.summary}
            </Typography>
          )}
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ pt: 0.5 }}>
            <Button
              onClick={() => navigate(routes.viewNews.path.replace(':id', current.id))}
              sx={{
                px: 2.5,
                py: 1,
                borderRadius: 2,
                fontWeight: 800,
                background: '#FFFFFF',
                color: '#0A0A0A',
                '&:hover': { background: '#FFFFFF', filter: 'brightness(0.94)' },
                '&:active': { transform: 'scale(0.98)' },
              }}>
              {t('readArticle', { ns: 'news', defaultValue: 'Leer artículo' })}
            </Button>
            <Button
              onClick={() => navigate(routes.listNews.path)}
              sx={{
                color: 'gold.bright',
                fontWeight: 700,
                textTransform: 'none',
                '&:hover': { backgroundColor: 'rgba(249,189,78,0.08)' },
              }}>
              {t('viewAll', { ns: 'news', defaultValue: 'Ver todas' })}
            </Button>
          </Stack>
          {items.length > 1 && (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ pt: 1 }}>
              <LinearProgress
                variant="determinate"
                value={((index + 1) / items.length) * 100}
                sx={{
                  flex: 1,
                  maxWidth: 260,
                  height: 4,
                  borderRadius: 2,
                  // Track fijo: el fondo de esta card es oscuro en ambos modos.
                  backgroundColor: 'rgba(255,255,255,0.14)',
                  '& .MuiLinearProgress-bar': { borderRadius: 2, backgroundColor: 'gold.bright' },
                }}
              />
              <IconButton
                size="small"
                onClick={() => goTo(-1)}
                aria-label={t('previous', { ns: 'common', defaultValue: 'Anterior' })}
                sx={{ color: 'rgba(255,255,255,0.72)', '&:hover': { color: '#FFFFFF' } }}>
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <Typography
                variant="caption"
                sx={{ color: 'rgba(255,255,255,0.72)', fontWeight: 700, whiteSpace: 'nowrap' }}>
                {index + 1} / {items.length}
              </Typography>
              <IconButton
                size="small"
                onClick={() => goTo(1)}
                aria-label={t('next', { ns: 'common', defaultValue: 'Siguiente' })}
                sx={{ color: 'rgba(255,255,255,0.72)', '&:hover': { color: '#FFFFFF' } }}>
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Stack>
          )}
        </Stack>

        {/* Imagen nítida */}
        {image && (
          <Box
            onClick={() => navigate(routes.viewNews.path.replace(':id', current.id))}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate(routes.viewNews.path.replace(':id', current.id));
              }
            }}
            aria-label={current.title}
            sx={{
              flexShrink: 0,
              width: { xs: '100%', md: '44%' },
              aspectRatio: '16 / 10',
              alignSelf: 'center',
              borderRadius: '14px',
              overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.14)',
              boxShadow: '0 12px 32px rgba(0, 0, 0, 0.35)',
              cursor: 'pointer',
              '& img': {
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                transition: 'transform 250ms cubic-bezier(0.32,0.72,0,1)',
              },
              '&:hover img': { transform: 'scale(1.03)' },
            }}>
            <img src={image} alt={current.title} />
          </Box>
        )}
      </Stack>
    </Box>
  );
}

export default FeaturedNewsCarousel;
